// ==========================================================
// Anexos de una convocatoria del SEACE (columna `url_anexos`)
// ==========================================================
// La columna llega como texto libre con el formato «nombre del archivo: URL»:
//
//   presupuesto completo.xlsx: https://drive.google.com/file/d/1Bhe…/view
//
// Hoy solo una convocatoria lo trae cargado (LP-ABR-10-2026-CS/MVES-1), así que
// no se sabe aún con qué separador vendrán varios. El parser acepta los cuatro
// candidatos razonables —salto de línea, `;`, `|` y tabulador— y, si no hay
// ninguno, trata todo el texto como un solo anexo. Añadir un separador después
// es cambiar un carácter en SEPARADORES.
const SEPARADORES = /\s*[\n;|\t]\s*/;

// «nombre: https://…». El nombre es perezoso para que corte en el primer `:`
// que deje una URL detrás, y no en el de «https:».
const CON_NOMBRE = /^(.*?)\s*:\s*(https?:\/\/\S+)\s*$/i;
const SOLO_URL = /https?:\/\/\S+/i;

// Extensión → cómo se describe el archivo en la ficha.
const TIPOS = [
  { test: /\.(xlsx?|xlsm|csv)$/i, tipo: 'excel', etiqueta: 'Hoja de cálculo' },
  { test: /\.pdf$/i, tipo: 'pdf', etiqueta: 'PDF' },
  { test: /\.(docx?|rtf|odt)$/i, tipo: 'word', etiqueta: 'Documento' },
  { test: /\.(zip|rar|7z)$/i, tipo: 'zip', etiqueta: 'Archivo comprimido' },
  { test: /\.(pptx?|odp)$/i, tipo: 'ppt', etiqueta: 'Presentación' },
  { test: /\.(dwg|dxf)$/i, tipo: 'plano', etiqueta: 'Plano' },
  { test: /\.(jpe?g|png|webp|gif)$/i, tipo: 'imagen', etiqueta: 'Imagen' }
];

const clasificar = (nombre) => {
  const encontrado = TIPOS.find(({ test }) => test.test(String(nombre || '').trim()));
  return encontrado
    ? { tipo: encontrado.tipo, etiqueta: encontrado.etiqueta }
    : { tipo: 'otro', etiqueta: 'Archivo adjunto' };
};

// Solo http/https, y sin credenciales incrustadas: la URL sale de un scraper y
// termina en un `window.open`.
const urlUsable = (valor) => {
  try {
    const url = new URL(String(valor || '').trim());
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    if (url.username || url.password) return null;
    return url.href;
  } catch (e) {
    return null;
  }
};

/**
 * Convierte el texto de `url_anexos` en una lista de anexos.
 * Devuelve [] cuando el campo está vacío o no trae ninguna URL usable.
 *
 * @returns {{nombre: string, url: string, tipo: string, etiqueta: string}[]}
 */
export const parseAnexos = (texto) => {
  const crudo = String(texto || '').trim();
  if (!crudo) return [];

  const anexos = [];
  const vistas = new Set();

  crudo.split(SEPARADORES).forEach((parte) => {
    const trozo = parte.trim();
    if (!trozo) return;

    const conNombre = trozo.match(CON_NOMBRE);
    // El grupo del nombre no puede ser a su vez una URL: eso pasaría con un
    // «http://a.pe: http://b.pe», que no es un nombre sino dos enlaces.
    const nombre = conNombre && !/^https?:\/\//i.test(conNombre[1].trim())
      ? conNombre[1].trim()
      : '';
    const posible = conNombre && nombre ? conNombre[2] : (trozo.match(SOLO_URL) || [])[0];

    const url = urlUsable(posible);
    if (!url || vistas.has(url)) return;
    vistas.add(url);

    const { tipo, etiqueta } = clasificar(nombre || url);
    anexos.push({ nombre: nombre || 'Anexo', url, tipo, etiqueta });
  });

  return anexos;
};

/**
 * Todos los documentos de una convocatoria, en el orden en que se ofrecen:
 * primero las bases y después los anexos de la entidad.
 *
 * Es lo que decide el botón de documentos del listado: con uno solo abre
 * directo, con varios despliega el selector.
 */
export const documentosDeConvocatoria = (proceso) => {
  const documentos = [];

  const bases = urlUsable(proceso?.url_doc);
  if (bases) {
    documentos.push({
      nombre: 'Bases del procedimiento',
      url: bases,
      tipo: 'pdf',
      etiqueta: 'PDF',
      esBases: true
    });
  }

  parseAnexos(proceso?.url_anexos).forEach((anexo) => {
    // Un anexo que repita la URL de las bases no se lista dos veces.
    if (documentos.some((d) => d.url === anexo.url)) return;
    documentos.push({ ...anexo, esBases: false });
  });

  return documentos;
};

export default parseAnexos;
